import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from "react";
import * as Sentry from "@sentry/react";
import dayjs from "dayjs";
import { useApi } from "../../hooks/useApi";
import { useStateValue } from "../../context/StateValueContext";
import { apiErrorMessage } from "../../utils/apiError";
import { EvidenceOrderBody } from "../../types/evidenceBankCase";
import { ReturnAddress } from "../../types/evidence";
import { InquiryDetail, InquiryFacts, InquiryMessage, InquiryTemplateItem, TemplateLanguage } from "./types";

type Outgoing = { text: string; templateKey: string | null; attachment: File | null; offerAmount: number | null; idempotencyKey: string; version: string | undefined; buyerName: string; message: InquiryMessage };

export type InquirySnackbar = { message: string; severity: 'success' | 'error'; viewId?: number; undo?: () => void } | null;

export type OfferBody = { offerType: string; amount: number; note: string; returnAddress?: ReturnAddress; idempotencyKey: string };

export type AcceptBody = { acceptType: string; amount?: number; note?: string; returnAddress?: ReturnAddress; idempotencyKey: string };

export const MAX_TEXT_LENGTH = 2000;

const ACTIONS = {
  offer: { failure: "Failed to send the offer" },
  accept: { failure: "Failed to accept the claim" },
};

const isNotFound = (err: unknown) => (err as { status?: number }).status === 404;

const getLoadedVersion = (detail: InquiryDetail | null, id: number) => (detail?.dispute.disputeId === id ? detail.version : undefined);

const isRefusal = (err: unknown) => {
  const status = (err as { status?: number }).status;
  return status !== undefined && status >= 400 && status < 500 && status !== 409;
};

function toMessageForm(text: string, templateKey: string | null, idempotencyKey: string, version: string | undefined, file: File): FormData {
  const form = new FormData();
  form.append("text", text);
  if (templateKey) form.append("templateKey", templateKey);
  form.append("idempotencyKey", idempotencyKey);
  if (version) form.append("version", version);
  form.append("file", file);
  return form;
}

const RECHECK_AFTER_MS = 90_000;
const POLL_AFTER_MS = 3000;
const MAX_POLLS = 6;
const HOLD_MS = 5000;

const longWords = (text: string) => new Set(text.toLowerCase().match(/\p{L}{4,}/gu) ?? []);

let outboxStore = new Map<number, Outgoing>();
const outboxListeners = new Set<() => void>();
let outboxOwner: string | null | undefined;

const subscribeOutbox = (listener: () => void) => {
  outboxListeners.add(listener);
  return () => { outboxListeners.delete(listener); };
};

const readOutbox = () => outboxStore;

const putOutgoing = (id: number, outgoing: Outgoing | null) => {
  const next = new Map(outboxStore);
  if (outgoing) next.set(id, outgoing);
  else next.delete(id);
  outboxStore = next;
  outboxListeners.forEach((listener) => listener());
};

export const clearOutbox = () => {
  outboxStore = new Map();
  outboxListeners.forEach((listener) => listener());
};

export function useInquiryDetail(disputeId: number | null) {
  const { get, loading } = useApi();
  const { post, postForm, put } = useApi();
  const [detail, setDetail] = useState<InquiryDetail | null>(null);
  const detailRef = useRef(detail);
  const [error, setError] = useState<string | null>(null);
  const [gone, setGone] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [templates, setTemplates] = useState<InquiryTemplateItem[]>([]);
  const [templatesError, setTemplatesError] = useState<string | null>(null);
  const [templatesKey, setTemplatesKey] = useState(0);
  const [pct, setPct] = useState(30);
  const [languageOverride, setLanguageOverride] = useState<TemplateLanguage | null>(null);
  const [templateLanguage, setTemplateLanguage] = useState<TemplateLanguage | null>(null);
  const loadedOverride = useRef<TemplateLanguage | null>(null);
  const [languageDispute, setLanguageDispute] = useState(disputeId);
  if (languageDispute !== disputeId) {
    setLanguageDispute(disputeId);
    setLanguageOverride(null);
  }
  const drafts = useRef(new Map<number, { text: string; templateKey: string | null }>());
  const [draft, setDraftState] = useState("");
  const [templateKey, setTemplateKey] = useState<string | null>(null);
  const latest = useRef({ draft, templateKey });
  const filled = useRef<string | null>(null);
  const [attachment, setAttachment] = useState<File | null>(null);
  const outbox = useSyncExternalStore(subscribeOutbox, readOutbox);
  const [{ user }] = useStateValue();
  const uid = user?.uid ?? null;
  const currentId = useRef(disputeId);
  const holds = useRef(new Map<number, { timer: ReturnType<typeof setTimeout>; fire: () => void; undo: () => void }>());
  const recheck = useRef<ReturnType<typeof setTimeout> | null>(null);
  const poll = useRef<ReturnType<typeof setTimeout> | null>(null);
  const polls = useRef(0);
  const requestSeq = useRef(0);
  const [polling, setPolling] = useState(false);
  const [snackbar, setSnackbar] = useState<InquirySnackbar>(null);
  const snackbarRef = useRef(snackbar);
  const waitingFailure = useRef<InquirySnackbar>(null);
  const [acting, setActing] = useState(false);
  const actingRef = useRef(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const assigningRef = useRef(false);

  useEffect(() => {
    currentId.current = disputeId;
    if (recheck.current) clearTimeout(recheck.current);
    recheck.current = null;
    if (poll.current) clearTimeout(poll.current);
    poll.current = null;
    polls.current = 0;
    setPolling(false);
    setDetail(null);
    setError(null);
    setGone(false);
    setTemplates([]);
    setTemplateLanguage(null);
    loadedOverride.current = null;
    setTemplatesError(null);
    setActionError(null);
    const saved = disputeId === null ? undefined : drafts.current.get(disputeId);
    setDraftState(saved?.text ?? "");
    setTemplateKey(saved?.templateKey ?? null);
    filled.current = null;
    setAttachment(null);
    setSnackbar((s) => (s?.undo ? null : s));
  }, [disputeId]);

  useEffect(() => () => {
    if (recheck.current) clearTimeout(recheck.current);
    if (poll.current) clearTimeout(poll.current);
  }, []);

  useEffect(() => {
    if (outboxOwner !== undefined && outboxOwner !== uid) clearOutbox();
    outboxOwner = uid;
  }, [uid]);

  useEffect(() => { latest.current = { draft, templateKey }; }, [draft, templateKey]);

  useEffect(() => { detailRef.current = detail; }, [detail]);

  useEffect(() => {
    snackbarRef.current = snackbar;
    const failure = waitingFailure.current;
    if (snackbar !== null || !failure) return;
    waitingFailure.current = null;
    if (failure.viewId !== currentId.current) setSnackbar(failure);
  }, [snackbar]);

  const keepDraft = useCallback((text: string, key: string | null) => {
    if (disputeId !== null) drafts.current.set(disputeId, { text, templateKey: key });
    setDraftState(text);
    setTemplateKey(key);
  }, [disputeId]);

  const follow = useCallback((id: number, prev: InquiryDetail) => {
    if (poll.current) clearTimeout(poll.current);
    poll.current = null;
    const more = (prev.refreshing || prev.factsRefreshing) && polls.current < MAX_POLLS;
    setPolling(more);
    if (!more) return;
    poll.current = setTimeout(() => {
      poll.current = null;
      polls.current += 1;
      const seen = requestSeq.current;
      get<InquiryDetail>(`/api/paypal-inquiries/${id}`)
        .then((res) => {
          if (currentId.current !== id || seen !== requestSeq.current) return;
          setDetail(res);
          if (prev.factsRefreshing && !res.factsRefreshing) setTemplatesKey((k) => k + 1);
          follow(id, res);
        })
        .catch((err) => {
          if (currentId.current !== id) return;
          setPolling(false);
          if (isNotFound(err)) { setGone(true); return; }
          Sentry.captureException(err);
        });
    }, POLL_AFTER_MS);
  }, [get]);

  const startManual = useCallback(() => {
    if (poll.current) clearTimeout(poll.current);
    poll.current = null;
    return ++requestSeq.current;
  }, []);

  const resume = useCallback((id: number, seq: number) => {
    if (currentId.current === id && seq === requestSeq.current && detailRef.current) follow(id, detailRef.current);
  }, [follow]);

  const refetch = useCallback((id: number, seq: number) => {
    get<InquiryDetail>(`/api/paypal-inquiries/${id}`)
      .then((res) => {
        if (currentId.current !== id || seq !== requestSeq.current) return;
        setDetail(res);
        follow(id, res);
      })
      .catch(() => resume(id, seq));
  }, [get, follow, resume]);

  useEffect(() => {
    if (disputeId === null) return;
    let current = true;
    if (poll.current) clearTimeout(poll.current);
    poll.current = null;
    const seen = requestSeq.current;
    get<InquiryDetail>(`/api/paypal-inquiries/${disputeId}`)
      .then((res) => { if (current && seen === requestSeq.current) { setDetail(res); setError(null); follow(disputeId, res); } })
      .catch((err) => {
        if (!current) return;
        if (isNotFound(err)) { setGone(true); return; }
        Sentry.captureException(err);
        setError(apiErrorMessage(err, "Failed to load the inquiry"));
      });
    return () => { current = false; };
  }, [get, disputeId, reloadKey, follow]);

  useEffect(() => {
    if (disputeId === null) return;
    let current = true;
    get<{ templates: InquiryTemplateItem[]; language: TemplateLanguage }>(`/api/paypal-inquiries/${disputeId}/templates?pct=${pct}${languageOverride ? `&lang=${languageOverride}` : ''}`)
      .then((res) => {
        if (!current) return;
        setTemplates(res.templates);
        setTemplateLanguage(res.language);
        loadedOverride.current = languageOverride;
        setTemplatesError(null);
        const applied = res.templates.find((t) => t.key === latest.current.templateKey);
        const text = latest.current.draft;
        if (!applied || (text !== applied.text && text !== filled.current)) return;
        filled.current = applied.text;
        if (text !== applied.text) keepDraft(applied.text, applied.key);
      })
      .catch((err) => {
        if (!current || isNotFound(err)) return;
        Sentry.captureException(err);
        setTemplatesError(apiErrorMessage(err, "Failed to load templates"));
        setLanguageOverride(loadedOverride.current);
      });
    return () => { current = false; };
  }, [get, disputeId, pct, languageOverride, templatesKey, keepDraft]);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  const setDraft = useCallback((text: string) => {
    const applied = filled.current ?? templates.find((t) => t.key === templateKey)?.text;
    const words = [...longWords(applied ?? "")];
    const present = longWords(text);
    const kept = text.trim() && (words.length === 0 || words.filter((w) => present.has(w)).length * 2 > words.length);
    keepDraft(text, kept ? templateKey : null);
  }, [keepDraft, templateKey, templates]);

  const applyTemplate = useCallback((template: InquiryTemplateItem) => {
    if (template.key === templateKey) {
      keepDraft(draft === filled.current ? "" : draft, null);
      filled.current = null;
      return;
    }
    if (draft.trim() && draft !== filled.current) {
      const prev = { id: disputeId, text: draft, key: templateKey };
      setSnackbar({
        message: "Template replaced your text",
        severity: 'success',
        undo: () => {
          if (currentId.current !== prev.id) return;
          keepDraft(prev.text, prev.key);
          filled.current = null;
          setSnackbar(null);
        },
      });
    }
    filled.current = template.text;
    keepDraft(template.text, template.key);
  }, [keepDraft, templateKey, draft, disputeId]);

  const landed = useCallback((id: number, next: InquiryDetail) => {
    if (currentId.current !== id) return;
    ++requestSeq.current;
    setDetail(next);
    follow(id, next);
    if (next.dispute.status !== 'UNDER_REVIEW') return;
    if (recheck.current) clearTimeout(recheck.current);
    recheck.current = setTimeout(() => {
      recheck.current = null;
      const seq = startManual();
      post<InquiryDetail>(`/api/paypal-inquiries/${id}/refresh`, {})
        .then((res) => {
          if (currentId.current !== id || seq !== requestSeq.current) return;
          setDetail(res);
          follow(id, res);
        })
        .catch(() => resume(id, seq));
    }, RECHECK_AFTER_MS);
  }, [post, follow, startManual, resume]);

  const deliver = useCallback(async (id: number, outgoing: Outgoing): Promise<boolean> => {
    putOutgoing(id, { ...outgoing, message: { ...outgoing.message, pending: true, failed: undefined } });
    const seq = currentId.current === id ? startManual() : null;
    try {
      const url = `/api/paypal-inquiries/${id}/message`;
      const res = outgoing.offerAmount !== null
        ? await post<{ detail: InquiryDetail }>(`/api/paypal-inquiries/${id}/offer`, { offerType: 'REFUND', amount: outgoing.offerAmount, note: outgoing.text, idempotencyKey: outgoing.idempotencyKey, version: outgoing.version })
        : outgoing.attachment
          ? await postForm<{ detail: InquiryDetail }>(url, toMessageForm(outgoing.text, outgoing.templateKey, outgoing.idempotencyKey, outgoing.version, outgoing.attachment))
          : await post<{ detail: InquiryDetail }>(url, { text: outgoing.text, templateKey: outgoing.templateKey ?? undefined, idempotencyKey: outgoing.idempotencyKey, version: outgoing.version });
      putOutgoing(id, null);
      landed(id, res.detail);
      setSnackbar((s) => (s?.undo ? s : { message: outgoing.offerAmount !== null ? "Offer sent" : "Reply sent", severity: 'success' }));
      return true;
    } catch (err) {
      if (isNotFound(err)) {
        if (seq !== null) resume(id, seq);
        putOutgoing(id, null);
        if (currentId.current === id) setGone(true);
        return false;
      }
      if (seq !== null) refetch(id, seq);
      Sentry.captureException(err);
      const reason = apiErrorMessage(err, outgoing.offerAmount !== null ? "Failed to send the offer" : "Failed to send the reply");
      putOutgoing(id, {
        ...outgoing,
        idempotencyKey: isRefusal(err) ? crypto.randomUUID() : outgoing.idempotencyKey,
        message: { ...outgoing.message, pending: false, failed: reason },
      });
      if (currentId.current !== id) {
        const failure = { message: `Reply to ${outgoing.buyerName} failed: ${reason}`, severity: 'error' as const, viewId: id };
        if (snackbarRef.current?.undo) waitingFailure.current = failure;
        else setSnackbar(failure);
      }
      return false;
    }
  }, [post, postForm, landed, startManual, resume, refetch]);

  const send = useCallback((offerAmount: number | null = null): Promise<boolean> | null => {
    if (disputeId === null || outboxStore.get(disputeId)?.message.pending) return null;
    const offer = offerAmount !== null && detail ? { amount: offerAmount, currency: detail.dispute.currency } : undefined;
    const message: InquiryMessage = {
      postedBy: 'SELLER', at: dayjs().toISOString(), text: draft, documents: [], sentBy: null,
      ...(offer ? { offer } : attachment && { attachmentName: attachment.name }),
    };
    const id = disputeId;
    const outgoing: Outgoing = {
      text: draft, templateKey, attachment, offerAmount: offer ? offerAmount : null,
      idempotencyKey: crypto.randomUUID(), version: getLoadedVersion(detail, id), buyerName: detail?.dispute.buyerName ?? 'the buyer', message: { ...message, pending: true, held: true },
    };
    setSnackbar((s) => (s?.undo ? null : s));
    putOutgoing(id, outgoing);
    drafts.current.delete(id);
    setDraftState("");
    setTemplateKey(null);
    setAttachment(null);
    return new Promise<boolean>((resolve) => {
      const fire = () => {
        holds.current.delete(id);
        resolve(deliver(id, { ...outgoing, message: { ...outgoing.message, held: undefined } }));
      };
      const undo = () => {
        clearTimeout(timer);
        holds.current.delete(id);
        putOutgoing(id, null);
        keepDraft(outgoing.text, outgoing.templateKey);
        filled.current = null;
        setAttachment(outgoing.attachment);
        resolve(false);
      };
      const timer = setTimeout(fire, HOLD_MS);
      holds.current.set(id, { timer, fire, undo });
    });
  }, [deliver, keepDraft, disputeId, draft, templateKey, attachment, detail]);

  const undoSend = useCallback(() => {
    if (currentId.current !== null) holds.current.get(currentId.current)?.undo();
  }, []);

  useEffect(() => {
    const guard = (event: BeforeUnloadEvent) => {
      if (holds.current.size === 0) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', guard);
    const pending = holds.current;
    return () => {
      window.removeEventListener('beforeunload', guard);
      for (const hold of [...pending.values()]) {
        clearTimeout(hold.timer);
        hold.fire();
      }
    };
  }, []);

  const retry = useCallback(() => {
    const outgoing = disputeId === null ? undefined : outboxStore.get(disputeId);
    if (disputeId === null || !outgoing || outgoing.message.pending) return null;
    return deliver(disputeId, { ...outgoing, version: getLoadedVersion(detailRef.current, disputeId) ?? outgoing.version });
  }, [deliver, disputeId]);

  const outgoing = disputeId === null ? undefined : outbox.get(disputeId);
  const messages = detail ? [...detail.messages, ...(outgoing ? [outgoing.message] : [])] : [];

  const submitAction = useCallback(async (kind: keyof typeof ACTIONS, body: OfferBody | AcceptBody): Promise<boolean> => {
    if (disputeId === null || actingRef.current) return false;
    actingRef.current = true;
    setActing(true);
    setActionError(null);
    const seq = startManual();
    const versioned = { ...body, version: getLoadedVersion(detailRef.current, disputeId) };
    try {
      const res = kind === 'offer'
        ? await post<{ detail: InquiryDetail }>(`/api/paypal-inquiries/${disputeId}/offer`, versioned)
        : await post<{ detail: InquiryDetail }>(`/api/paypal-inquiries/${disputeId}/accept`, versioned);
      landed(disputeId, res.detail);
      return true;
    } catch (err) {
      if (isNotFound(err)) {
        resume(disputeId, seq);
        if (currentId.current === disputeId) setGone(true);
      } else {
        refetch(disputeId, seq);
        Sentry.captureException(err);
        setActionError(apiErrorMessage(err, ACTIONS[kind].failure));
      }
      return false;
    } finally {
      actingRef.current = false;
      setActing(false);
    }
  }, [post, disputeId, landed, startManual, resume, refetch]);

  const refreshFacts = useCallback(async () => {
    if (disputeId === null) return;
    setRefreshing(true);
    const seq = startManual();
    try {
      const res = await post<{ facts: InquiryFacts; at: string }>(`/api/paypal-inquiries/${disputeId}/facts/refresh`, {});
      const prev = detailRef.current;
      if (currentId.current === disputeId && seq === requestSeq.current && prev) {
        const next = { ...prev, facts: res.facts, factsAt: res.at, factsRefreshing: false };
        setDetail(next);
        setTemplatesKey((k) => k + 1);
        follow(disputeId, next);
      }
    } catch (err) {
      resume(disputeId, seq);
      if (isNotFound(err)) {
        if (currentId.current === disputeId) setGone(true);
      } else {
        Sentry.captureException(err);
        setSnackbar({ message: apiErrorMessage(err, "Could not reload the order"), severity: 'error' });
      }
    } finally {
      setRefreshing(false);
    }
  }, [post, disputeId, startManual, follow, resume]);

  const attachOrder = useCallback(async (body: EvidenceOrderBody) => {
    if (disputeId === null) return;
    polls.current = 0;
    const seq = startManual();
    let res: InquiryDetail;
    try {
      res = await put<InquiryDetail>(`/api/paypal-inquiries/${disputeId}/order`, body, { slowApi: true });
    } catch (err) {
      resume(disputeId, seq);
      throw err;
    }
    if (currentId.current === disputeId && seq === requestSeq.current) {
      setDetail(res);
      setTemplatesKey((k) => k + 1);
      follow(disputeId, res);
    }
    setSnackbar({ message: "Order attached", severity: 'success' });
  }, [put, disputeId, follow, startManual, resume]);

  const sync = useCallback(async (): Promise<boolean> => {
    if (disputeId === null) return false;
    setSyncing(true);
    polls.current = 0;
    const seq = startManual();
    try {
      const res = await post<InquiryDetail>(`/api/paypal-inquiries/${disputeId}/refresh`, {});
      if (currentId.current === disputeId && seq === requestSeq.current) {
        setDetail(res);
        follow(disputeId, res);
      }
      return true;
    } catch (err) {
      resume(disputeId, seq);
      if (isNotFound(err)) {
        if (currentId.current === disputeId) setGone(true);
      } else {
        Sentry.captureException(err);
        setSnackbar({ message: apiErrorMessage(err, "Failed to refresh the inquiry"), severity: 'error' });
      }
      return false;
    } finally {
      setSyncing(false);
    }
  }, [post, disputeId, follow, startManual, resume]);

  const assign = useCallback(async (userId: number | null): Promise<boolean> => {
    if (disputeId === null || assigningRef.current) return false;
    assigningRef.current = true;
    setAssigning(true);
    const seq = startManual();
    try {
      const res = await post<InquiryDetail>(`/api/paypal-inquiries/${disputeId}/assign`, { userId });
      if (currentId.current === disputeId && seq === requestSeq.current) {
        setDetail(res);
        follow(disputeId, res);
      }
      setSnackbar({ message: res.dispute.assignedUserId === null ? "Unassigned" : `Assigned to ${res.dispute.assignedFirstName}`, severity: 'success' });
      return true;
    } catch (err) {
      resume(disputeId, seq);
      if (isNotFound(err)) {
        if (currentId.current === disputeId) setGone(true);
      } else {
        Sentry.captureException(err);
        setSnackbar({ message: apiErrorMessage(err, "Failed to assign"), severity: 'error' });
      }
      return false;
    } finally {
      assigningRef.current = false;
      setAssigning(false);
    }
  }, [post, disputeId, follow, startManual, resume]);

  return {
    assign, assigning, submitAction, acting, actionError, setActionError, refreshFacts, refreshing, attachOrder, sync, syncing, polling,
    detail, loading, error, gone, reload, templates, templatesError, pct, setPct, language: languageOverride ?? templateLanguage, languageDetected: languageOverride === null, setLanguage: setLanguageOverride,
    draft, setDraft, templateKey, templateUntouched: templateKey !== null && draft === filled.current, applyTemplate, attachment, setAttachment, send, undoSend, retry, messages, snackbar, setSnackbar,
  };
}

export type InquiryDetailState = ReturnType<typeof useInquiryDetail>;
